import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/api';
import '../../styles/admin/AddProjects.css';
import ProtectedAdminRoute from '../../components/ProtectedAdminRoute';

export default function RecruitmentSettings() {
  const [settings, setSettings] = useState({
    isOpen: false,
    googleFormUrl: '',
    message: '',
    showAdmissionInfo: false,
    deadline: '',
    tryoutStart: '',
    tryoutEnd: '',
    contactEmail: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/api/recruitment');
      setSettings(data);
    } catch (err) {
      console.error('Error fetching recruitment settings:', err);
      setMessage('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await apiFetch('/api/recruitment', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });
      
      if (response.success) {
        setMessage('Settings updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      setMessage('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <ProtectedAdminRoute>
        <div className="add-project-flex">
          <div className="add-project-form">
            <h2>Recruitment Settings</h2>
            <p>Loading...</p>
          </div>
        </div>
      </ProtectedAdminRoute>
    );
  }

  return (
    <ProtectedAdminRoute>
      <div className="add-project-flex">
        <div className="add-project-form">
          <h2>Recruitment Settings</h2>
          
          {message && (
            <div className={`admin-message ${message.includes('success') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  name="isOpen"
                  checked={settings.isOpen}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                <span className="checkbox-label">Recruitment Open</span>
              </label>
              <p className="form-help">Toggle to open or close recruitment applications</p>
            </div>

            <div className="form-group">
              <label htmlFor="googleFormUrl">Google Form URL</label>
              <input
                type="url"
                id="googleFormUrl"
                name="googleFormUrl"
                value={settings.googleFormUrl}
                onChange={handleInputChange}
                placeholder="https://forms.google.com/..."
                disabled={!settings.isOpen}
                className={!settings.isOpen ? 'disabled' : ''}
                autoComplete="off"
              />
              <p className="form-help">Enter the Google Form URL for applications (only used when recruitment is open)</p>
            </div>

            <div className="form-group">
              <label htmlFor="message">Custom Message</label>
              <textarea
                id="message"
                name="message"
                value={settings.message}
                onChange={handleInputChange}
                rows="4"
                placeholder="Enter a custom message for the recruitment page..."
                className="form-textarea"
              />
              <p className="form-help">Optional: Custom message to display on the recruitment page</p>
            </div>

            <div className="form-section">
              <h3>Admission Information</h3>
              
              <div className="form-group">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    name="showAdmissionInfo"
                    checked={settings.showAdmissionInfo}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-label">Show Admission Info</span>
                </label>
                <p className="form-help">Display admission timeline and contact information when recruitment is open</p>
              </div>

              <div className="form-group">
                <label htmlFor="deadline">Application Deadline</label>
                <input
                  type="text"
                  id="deadline"
                  name="deadline"
                  value={settings.deadline}
                  onChange={handleInputChange}
                  placeholder="e.g., 24.08.2025 | 23:59"
                  disabled={!settings.showAdmissionInfo}
                  className={!settings.showAdmissionInfo ? 'disabled' : ''}
                  autoComplete="off"
                />
                <p className="form-help">Format: DD.MM.YYYY | HH:MM</p>
              </div>

              <div className="form-group">
                <label htmlFor="tryoutStart">Try-out Start</label>
                <input
                  type="text"
                  id="tryoutStart"
                  name="tryoutStart"
                  value={settings.tryoutStart}
                  onChange={handleInputChange}
                  placeholder="e.g., 27.08.2025 | 08:00"
                  disabled={!settings.showAdmissionInfo}
                  className={!settings.showAdmissionInfo ? 'disabled' : ''}
                  autoComplete="off"
                />
                <p className="form-help">Format: DD.MM.YYYY | HH:MM</p>
              </div>

              <div className="form-group">
                <label htmlFor="tryoutEnd">Try-out End</label>
                <input
                  type="text"
                  id="tryoutEnd"
                  name="tryoutEnd"
                  value={settings.tryoutEnd}
                  onChange={handleInputChange}
                  placeholder="e.g., 29.08.2025 | 21:00"
                  disabled={!settings.showAdmissionInfo}
                  className={!settings.showAdmissionInfo ? 'disabled' : ''}
                  autoComplete="off"
                />
                <p className="form-help">Format: DD.MM.YYYY | HH:MM</p>
              </div>

              <div className="form-group">
                <label htmlFor="contactEmail">Contact Email</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={settings.contactEmail}
                  onChange={handleInputChange}
                  placeholder="e.g., s203506@nhhs.no"
                  disabled={!settings.showAdmissionInfo}
                  className={!settings.showAdmissionInfo ? 'disabled' : ''}
                  autoComplete="off"
                />
                <p className="form-help">Contact email for admission inquiries</p>
              </div>
            </div>

            <div className="form-group">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>

          <div className="recruitment-preview">
            <h3>Preview</h3>
            <div className="preview-content">
              <div className="preview-item">
                <strong>Status:</strong> 
                <span className={`status-badge ${settings.isOpen ? 'open' : 'closed'}`}>
                  {settings.isOpen ? '🟢 Open' : '🔴 Closed'}
                </span>
              </div>
              <div className="preview-item">
                <strong>Form URL:</strong> {settings.googleFormUrl || 'Not set'}
              </div>
              <div className="preview-item">
                <strong>Message:</strong> {settings.message || 'Default message will be used'}
              </div>
              <div className="preview-item">
                <strong>Show Admission Info:</strong> 
                <span className={`status-badge ${settings.showAdmissionInfo ? 'open' : 'closed'}`}>
                  {settings.showAdmissionInfo ? '✅ Yes' : '❌ No'}
                </span>
              </div>
              {settings.showAdmissionInfo && (
                <>
                  <div className="preview-item">
                    <strong>Deadline:</strong> {settings.deadline || 'Not set'}
                  </div>
                  <div className="preview-item">
                    <strong>Try-out Start:</strong> {settings.tryoutStart || 'Not set'}
                  </div>
                  <div className="preview-item">
                    <strong>Try-out End:</strong> {settings.tryoutEnd || 'Not set'}
                  </div>
                  <div className="preview-item">
                    <strong>Contact:</strong> {settings.contactEmail || 'Not set'}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ProtectedAdminRoute>
  );
}
